import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import PageAdvice from "../components/PageAdvice";
import {
  CircleNotch, Database, Eye, CheckCircle, WarningCircle,
  CaretRight, MagnifyingGlass, Key, CaretDown, FloppyDisk, ArrowSquareOut,
} from "@phosphor-icons/react";

const AROFLO_V1_DOCS = "https://apidocs.aroflo.com/";

const CRED_FIELDS = [
  { key: "base_url", label: "Base URL", placeholder: "https://api.aroflo.com/", help: "AroFlo legacy API endpoint." },
  { key: "uencode", label: "uEncoded", placeholder: "uEncoded value from AroFlo", help: "Encoded username (Site Admin → Settings → AroFlo API)." },
  { key: "pencode", label: "pEncoded", placeholder: "pEncoded value from AroFlo", help: "Encoded API key / application key." },
  { key: "orgencode", label: "orgEncoded", placeholder: "orgEncoded value from AroFlo", help: "Encoded organisation identifier." },
  { key: "secret", label: "API Secret Key", placeholder: "API Secret Key from AroFlo", help: "Used to sign each request (never sent in plain text)." },
];

function CredentialsPanel({ onSaved }) {
  const [creds, setCreds] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    api.get("/legacy/credentials").then((r) => setCreds(r.data)).catch(() => setCreds({}));
  }, []);

  const set = (k, v) => setCreds((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(CRED_FIELDS.map((f) => [f.key, creds[f.key] || ""]));
      const r = await api.put("/legacy/credentials", payload);
      setFlash(true); setTimeout(() => setFlash(false), 2200);
      onSaved?.(r.data.status);
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-6 rounded-sm border border-neutral-200 bg-white shadow-sm" data-testid="legacy-credentials-panel">
      <button onClick={() => setOpen((o) => !o)} data-testid="legacy-credentials-toggle"
        className="flex w-full items-center justify-between px-5 py-4 text-left">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-sm bg-[#0055FF]/10 text-[#0055FF]"><Key size={18} weight="bold" /></div>
          <div>
            <p className="font-semibold text-[#0A0A0A]">API credentials</p>
            <p className="text-sm text-neutral-500">
              Enter or update the AroFlo legacy credentials{creds?.source ? ` · currently from ${creds.source}` : ""}.
            </p>
          </div>
        </div>
        <CaretDown size={18} className={`text-neutral-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-neutral-200 p-5">
          {creds === null ? (
            <div className="flex h-20 items-center justify-center"><CircleNotch size={22} className="animate-spin text-[#0055FF]" /></div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                {CRED_FIELDS.map((f) => {
                  const isSecret = f.key !== "base_url";
                  const configured = creds.configured?.[f.key];
                  const preview = creds.previews?.[f.key];
                  return (
                    <div key={f.key} className={f.key === "base_url" ? "sm:col-span-2" : ""}>
                      <label className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-neutral-700">
                        {f.label}
                        {isSecret && configured && (
                          <span className="inline-flex items-center gap-1 rounded-sm bg-[#059669]/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#059669]" data-testid={`legacy-cred-${f.key}-configured`}>
                            <CheckCircle size={11} weight="fill" /> Set{preview ? ` · ${preview}` : ""}
                          </span>
                        )}
                      </label>
                      <input
                        data-testid={`legacy-cred-${f.key}`}
                        type={isSecret ? "password" : "text"}
                        value={creds[f.key] || ""}
                        onChange={(e) => set(f.key, e.target.value)}
                        placeholder={isSecret && configured ? "Stored securely — leave blank to keep" : f.placeholder}
                        spellCheck={false}
                        autoComplete="off"
                        className="w-full rounded-sm border border-neutral-300 bg-white px-3 py-2.5 font-mono text-sm outline-none focus:border-[#0055FF]"
                      />
                      <p className="mt-1 text-xs text-neutral-400">{f.help}</p>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 flex items-center justify-end gap-3">
                <button data-testid="legacy-cred-save" onClick={save} disabled={saving}
                  className="inline-flex items-center gap-2 rounded-sm bg-[#059669] px-4 py-2 text-sm font-semibold text-white hover:bg-[#047857] disabled:opacity-60">
                  {saving ? <CircleNotch size={15} className="animate-spin" /> : flash ? <CheckCircle size={15} weight="fill" /> : <FloppyDisk size={15} weight="bold" />}
                  {saving ? "Testing…" : flash ? "Saved & tested" : "Save & test connection"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function StatusBanner({ status }) {
  if (!status) {
    return (
      <div className="mt-6 flex items-center gap-3 rounded-sm border border-neutral-200 bg-white p-4" data-testid="legacy-status-loading">
        <CircleNotch size={18} className="animate-spin text-[#0055FF]" />
        <span className="text-sm text-neutral-600">Probing legacy API connection…</span>
      </div>
    );
  }
  const connected = status.connected;
  const Icon = connected ? CheckCircle : WarningCircle;
  const tone = connected ? "border-[#059669]/30 bg-[#059669]/5" : "border-[#D97706]/30 bg-[#D97706]/5";
  const iconColor = connected ? "text-[#059669]" : "text-[#D97706]";
  return (
    <div className={`mt-6 rounded-sm border p-4 ${tone}`} data-testid="legacy-status-banner">
      <div className="flex items-start gap-3">
        <Icon size={20} weight="fill" className={`mt-0.5 shrink-0 ${iconColor}`} />
        <div className="min-w-0">
          <p className="font-semibold text-[#0A0A0A]" data-testid="legacy-status-headline">
            {connected ? "Connected to Aroflo API (v1)" : "Aroflo API (v1) not authenticated"}
          </p>
          <p className="mt-0.5 text-sm text-neutral-600">
            {connected
              ? "HMAC-SHA512 handshake accepted — live zone data is available below."
              : status.status_message || "The AroFlo legacy endpoint rejected the request."}
          </p>
          <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-neutral-500">
            <span>Base URL: <span className="font-mono">{status.base_url}</span></span>
            {status.uencode_preview && <span>uEncoded: <span className="font-mono">{status.uencode_preview}</span></span>}
            {status.status && <span>API status: <span className="font-mono">{status.status}</span></span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function ZoneCard({ zone, snapshot, onOpen }) {
  return (
    <button data-testid={`legacy-zone-${zone.id}`} onClick={onOpen}
      className="group flex items-start justify-between gap-3 rounded-sm border border-neutral-200 bg-white p-4 text-left shadow-sm transition-all duration-200 hover:border-[#0055FF] hover:shadow-md">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <Database size={16} weight="bold" className="text-[#0055FF]" />
          <p className="font-semibold text-[#0A0A0A]">{zone.label}</p>
        </div>
        <p className="mt-1 font-mono text-xs text-neutral-400">zone={zone.id}</p>
        <p className="mt-1 text-sm text-neutral-600">{zone.description}</p>
        {snapshot && (
          <p className="mt-2 inline-flex items-center gap-1 rounded-sm bg-[#059669]/10 px-2 py-0.5 text-xs font-semibold text-[#059669]" data-testid={`legacy-zone-synced-${zone.id}`}>
            <CheckCircle size={12} weight="fill" /> {snapshot.count?.toLocaleString() ?? "—"} synced to DB
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1 text-neutral-300 group-hover:text-[#0055FF]">
        <Eye size={16} />
        <CaretRight size={14} />
      </div>
    </button>
  );
}

export default function LegacyApi() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [sync, setSync] = useState(null);
  const [query, setQuery] = useState("");
  const [section, setSection] = useState("connected"); // connected | possible | catalog

  useEffect(() => {
    api.get("/legacy/zones").then((r) => setData(r.data)).catch(() => setData({ zones: [], groups: [] }));
    api.get("/legacy/status").then((r) => setStatus(r.data)).catch(() => setStatus({ connected: false }));
    api.get("/legacy/sync").then((r) => setSync(r.data)).catch(() => setSync({ config: { zones: [] }, snapshots: {} }));
  }, []);

  const syncedSet = useMemo(() => new Set(sync?.config?.zones || []), [sync]);
  const snapshots = sync?.snapshots || {};

  const counts = useMemo(() => {
    if (!data?.zones) return { connected: 0, possible: 0, catalog: 0 };
    const connected = data.zones.filter((z) => syncedSet.has(z.id)).length;
    return { connected, possible: data.zones.length - connected, catalog: data.zones.length };
  }, [data, syncedSet]);

  const grouped = useMemo(() => {
    if (!data?.zones) return [];
    const q = query.trim().toLowerCase();
    const map = {};
    data.zones
      .filter((z) => {
        if (section === "connected" && !syncedSet.has(z.id)) return false;
        if (section === "possible" && syncedSet.has(z.id)) return false;
        if (!q) return true;
        return [z.label, z.id, z.description].join(" ").toLowerCase().includes(q);
      })
      .forEach((z) => { (map[z.group] = map[z.group] || []).push(z); });
    return (data.groups || []).filter((g) => map[g]).map((g) => ({ group: g, items: map[g] }));
  }, [data, query, section, syncedSet]);

  const SECTIONS = [
    { id: "connected", label: "Connected now", count: counts.connected },
    { id: "possible", label: "Possible (not connected)", count: counts.possible },
    { id: "catalog", label: "Full catalog", count: counts.catalog },
  ];

  const SECTION_BLURB = {
    connected: "Zones Asset Hub is actively syncing from the Aroflo v1 (legacy) API into the local database.",
    possible: "Zones the v1 API exposes that aren't being synced yet — open one to browse its live data, or enable it on the Scheduler page.",
    catalog: "Every Aroflo v1 (legacy) data zone. Open a zone to browse live records; see the official docs for field reference.",
  };

  return (
    <Layout>
      <div data-testid="legacy-api-page">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">Integration</p>
            <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">Aroflo API (v1)</h1>
            <p className="mt-2 max-w-3xl text-base text-neutral-600">
              The classic <span className="font-semibold">HMAC-SHA512</span> API. See what's
              <span className="font-semibold text-[#0055FF]"> connected now</span>, what's
              <span className="font-semibold text-[#7C3AED]"> possible but not connected</span>, and the
              <span className="font-semibold"> full catalog</span> of zones with links to the official Aroflo docs.
            </p>
          </div>
          <a href={AROFLO_V1_DOCS} target="_blank" rel="noreferrer" data-testid="aroflo-v1-docs-link"
            className="inline-flex items-center gap-2 rounded-sm border border-neutral-300 bg-white px-4 py-2 text-sm font-semibold text-neutral-700 transition-all duration-200 hover:border-[#0055FF] hover:text-[#0055FF]">
            <ArrowSquareOut size={16} weight="bold" /> API documentation
          </a>
        </div>

        <StatusBanner status={status} />
        <CredentialsPanel onSaved={(st) => st && setStatus(st)} />

        {data === null ? (
          <div className="mt-10 flex h-40 items-center justify-center"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div>
        ) : (
          <>
            {/* Section tabs */}
            <div className="mt-8 flex flex-wrap gap-1.5 border-b border-neutral-200">
              {SECTIONS.map((s) => (
                <button key={s.id} data-testid={`v1-section-${s.id}`} onClick={() => setSection(s.id)}
                  className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${section === s.id ? "border-[#0055FF] text-[#0055FF]" : "border-transparent text-neutral-500 hover:text-neutral-800"}`}>
                  {s.label}
                  <span className={`rounded-sm px-1.5 py-0.5 text-xs font-bold ${section === s.id ? "bg-[#0055FF]/10 text-[#0055FF]" : "bg-neutral-100 text-neutral-500"}`}>{s.count}</span>
                </button>
              ))}
            </div>

            <p className="mt-3 max-w-3xl text-sm text-neutral-600">{SECTION_BLURB[section]}</p>

            <div className="mt-4 relative max-w-md">
              <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input data-testid="legacy-zone-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search zones…"
                className="w-full rounded-sm border border-neutral-300 bg-white py-2.5 pl-11 pr-4 text-base outline-none focus:border-[#0055FF]" />
            </div>

            {grouped.length === 0 ? (
              <div className="mt-6 rounded-sm border border-dashed border-neutral-300 bg-white p-10 text-center" data-testid="v1-empty">
                <p className="text-sm text-neutral-500">
                  {section === "connected" ? "No zones are being synced yet — enable zones on the Scheduler page." : "Nothing matches your search."}
                </p>
              </div>
            ) : (
              <div className="mt-6 space-y-6">
                {grouped.map((g) => (
                  <div key={g.group} data-testid={`legacy-group-${g.group}`}>
                    <h2 className="mb-2 text-sm font-bold uppercase tracking-widest text-neutral-500">{g.group}</h2>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {g.items.map((z) => (
                        <ZoneCard key={z.id} zone={z} snapshot={snapshots[z.id]} onOpen={() => navigate(`/legacy-api/${z.id}`)} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <PageAdvice items={[
              "‘Connected now’ lists the zones configured for the scheduled v1 sync (managed on the Scheduler page); ‘Possible (not connected)’ is everything else the v1 API exposes.",
              "Open any zone to browse its live records with server-side search, sorting and date filters — regardless of whether it's being synced.",
              "v1 is the classic HMAC-SHA512 API kept separate from the v2 Bearer proxy. See the official Aroflo docs for the full field reference.",
            ]} />
          </>
        )}
      </div>
    </Layout>
  );
}
